import { useEffect, useState } from "react";
import { Loader2, Warehouse, BellDot } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { warehousesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const fmtHour = (h) => {
  const suffix = h < 12 ? "AM" : "PM";
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}:00 ${suffix}`;
};

const EMPTY = {
  name: "",
  code: "",
  status: "active",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  notes: "",
  after_hours_enabled: true,
  after_hours_start: 8,
  after_hours_end: 18,
  after_hours_include_weekends: true,
};

export function WarehouseDrawer({ open, onOpenChange, record, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? {
              name: record.name || "",
              code: record.code || "",
              status: record.status || "active",
              contact_name: record.contact_name || "",
              contact_email: record.contact_email || "",
              contact_phone: record.contact_phone || "",
              address: record.address || "",
              notes: record.notes || "",
              after_hours_enabled: record.after_hours_enabled ?? true,
              after_hours_start: record.after_hours_start ?? 8,
              after_hours_end: record.after_hours_end ?? 18,
              after_hours_include_weekends: record.after_hours_include_weekends ?? true,
            }
          : EMPTY
      );
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const nameError = !form.name.trim() ? "Warehouse name is required" : "";
  const canSave = !nameError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        code: form.code.trim(),
        status: form.status,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        after_hours_enabled: !!form.after_hours_enabled,
        after_hours_start: Number(form.after_hours_start),
        after_hours_end: Number(form.after_hours_end),
        after_hours_include_weekends: !!form.after_hours_include_weekends,
      };
      const res = isEdit
        ? await warehousesService.update(record.id, body)
        : await warehousesService.create(body);
      toast.success(isEdit ? "Warehouse updated" : "Warehouse created");
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Warehouse}
      title={isEdit ? "Edit Warehouse" : "New Warehouse"}
      description={
        isEdit
          ? "Update this warehouse organization."
          : "Onboard a warehouse (3PL) as a tenant. Then invite its 3PL Owner from the Users module."
      }
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="warehouse-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="warehouse-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create warehouse"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Warehouse details">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Warehouse Name" required error={nameError}>
              <Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Sunrise Fulfillment" data-testid="warehouse-name-input" />
            </Field>
            <Field label="Code" hint="Short internal identifier.">
              <Input value={form.code} onChange={(e) => set("code")(e.target.value)} placeholder="SUN" data-testid="warehouse-code-input" />
            </Field>
          </div>
          <Field label="Status">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="warehouse-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </DrawerSection>

        <DrawerSection title="Contact">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Contact Name">
              <Input value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} placeholder="Sam Rivera" data-testid="warehouse-contact-name-input" />
            </Field>
            <Field label="Contact Email">
              <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email")(e.target.value)} placeholder="ops@sunrise3pl.com" data-testid="warehouse-contact-email-input" />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Contact Phone">
              <Input value={form.contact_phone} onChange={(e) => set("contact_phone")(e.target.value)} placeholder="+1 (555) 000-0000" data-testid="warehouse-contact-phone-input" />
            </Field>
            <Field label="Address">
              <Input value={form.address} onChange={(e) => set("address")(e.target.value)} placeholder="120 Dock St, Austin TX" data-testid="warehouse-address-input" />
            </Field>
          </div>
        </DrawerSection>

        <DrawerSection title="Notes">
          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} placeholder="Internal notes about this warehouse…" data-testid="warehouse-notes-input" />
          </Field>
        </DrawerSection>

        <DrawerSection title="After-hours order alerts">
          <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 p-4">
            <BellDot className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">Email warehouse managers</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    When an order lands outside business hours, ping every active Warehouse Manager here so nothing sits overnight.
                  </p>
                </div>
                <Switch
                  checked={form.after_hours_enabled}
                  onCheckedChange={(v) => set("after_hours_enabled")(v)}
                  data-testid="warehouse-after-hours-enabled"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Business hours start" hint="Local time (per company timezone).">
              <Select
                value={String(form.after_hours_start)}
                onValueChange={(v) => set("after_hours_start")(Number(v))}
                disabled={!form.after_hours_enabled}
              >
                <SelectTrigger data-testid="warehouse-ah-start">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Business hours end" hint="Alerts fire before start or from end.">
              <Select
                value={String(form.after_hours_end)}
                onValueChange={(v) => set("after_hours_end")(Number(v))}
                disabled={!form.after_hours_enabled}
              >
                <SelectTrigger data-testid="warehouse-ah-end">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Include weekends</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Sat & Sun are treated entirely as after-hours.</p>
            </div>
            <Switch
              checked={form.after_hours_include_weekends}
              onCheckedChange={(v) => set("after_hours_include_weekends")(v)}
              disabled={!form.after_hours_enabled}
              data-testid="warehouse-ah-weekends"
            />
          </div>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
